import React, { useMemo } from 'react';
import {
  Table as MuiTable,
  TableContainer,
  TablePagination,
  Paper,
} from '@mui/material';
import { TableProps, TableColumn } from './Table.props';
import { TableHeader } from './TableHeader/TableHeader';
import { TableBody } from './TableBody/TableBody';
import { useTableSelection } from './hooks/useTableSelection';
import { Checkbox } from '@/components/atoms';

/**
 * Table Component - A feature-rich table with selection, sorting, pagination, and actions
 *
 * @template T - The type of data rows
 *
 * Features:
 * - Row selection (single/multi)
 * - Sortable columns
 * - Pagination
 * - Row actions menu
 * - Custom loading and empty states
 * - Sticky header
 * - Striped rows
 * - Hover effects
 * - Dynamic height calculation
 * - Configurable table layout (auto/fixed)
 *
 * @example
 * ```tsx
 * <Table
 *   columns={columns}
 *   rows={data}
 *   selectable
 *   hover
 *   tableLayout="auto"
 * />
 * ```
 */
export const Table = <T = Record<string, unknown>,>({
  columns,
  rows,
  onRowClick,
  allowRowClick = false,
  pagination,
  sorting,
  loading = false,
  loadingComponent,
  emptyComponent,
  stickyHeader = true,
  striped = false,
  hover = true,
  tableWidth = '100%',
  tableHeight,
  rowHeight = 48,
  tableLayout = 'fixed',
  selectable = false,
  selectedRows,
  onSelectionChange,
  getRowId,
  ...tableProps
}: TableProps<T>) => {
  // Calculate dynamic table container height
  const dynamicTableContainerHeight = useMemo(() => {
    if (tableHeight) return tableHeight;

    const targetRowCount =
      pagination?.rowsPerPage || Math.min(rows.length, 5) || 3;
    const headerHeight = 56;
    return headerHeight + targetRowCount * rowHeight;
  }, [tableHeight, pagination?.rowsPerPage, rows.length, rowHeight]);

  // Build columns array with selection column if enabled
  const allColumns = useMemo(() => {
    const cols: TableColumn<T>[] = [];

    // Add selection column at the start if enabled
    if (selectable) {
      cols.push({
        id: '__select',
        label: '',
        align: 'left',
        width: 58,
        sticky: 'left', // Selection column is always sticky left
        render: (_: unknown, row: T, index: number) => {
          const rowId = getRowId ? getRowId(row) : index;
          const isSelected = selectedRows?.has(rowId) || false;
          return (
            <Checkbox
              checked={isSelected}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (onSelectionChange && getRowId) {
                  const currentSelectedRows = new Set(selectedRows);
                  if (event.target.checked) {
                    currentSelectedRows.add(rowId);
                  } else {
                    currentSelectedRows.delete(rowId);
                  }
                  onSelectionChange(currentSelectedRows);
                }
              }}
            />
          );
        },
      });
    }

    // Add all columns (including any action columns defined by users)
    cols.push(...columns);

    return cols;
  }, [columns, selectable, selectedRows, onSelectionChange, getRowId]);

  // Calculate sticky positions for columns
  const columnStickyPositions = useMemo(() => {
    const positions = new Map<string | keyof T, number>();
    let leftOffset = 0;
    let rightOffset = 0;

    // Calculate left sticky positions
    for (const column of allColumns) {
      if (column.sticky === 'left') {
        positions.set(column.id, leftOffset);
        const width = typeof column.width === 'number' ? column.width : 100;
        leftOffset += width;
      }
    }

    // Calculate right sticky positions (from right to left)
    for (let i = allColumns.length - 1; i >= 0; i--) {
      const column = allColumns[i];
      if (column.sticky === 'right') {
        positions.set(column.id, rightOffset);
        const width = typeof column.width === 'number' ? column.width : 100;
        rightOffset += width;
      }
    }

    return positions;
  }, [allColumns]);

  const {
    selectedRowIds,
    isAllSelected,
    isIndeterminate,
    getRowIdentifier,
    handleSelectAll,
  } = useTableSelection({
    rows,
    selectedRows,
    onSelectionChange,
    getRowId,
  });

  return (
    <>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          width: tableWidth,
          height: dynamicTableContainerHeight,
          maxHeight: dynamicTableContainerHeight,
          boxShadow: 'none',
          border: 'none',
        }}
      >
        <MuiTable
          stickyHeader={stickyHeader}
          size="medium"
          sx={{
            minWidth: tableWidth === '100%' ? 650 : tableWidth,
            tableLayout: tableLayout === 'fixed' ? 'fixed' : 'auto',
          }}
          {...tableProps}
        >
          <TableHeader
            columns={allColumns}
            sorting={sorting}
            allSelected={isIndeterminate ? undefined : isAllSelected}
            onSelectAll={handleSelectAll}
            stickyHeader={stickyHeader}
            columnStickyPositions={columnStickyPositions}
          />

          <TableBody
            rows={rows}
            columns={allColumns}
            loading={loading}
            loadingComponent={loadingComponent}
            noDataComponent={emptyComponent}
            columnStickyPositions={columnStickyPositions}
            selectedRowIds={selectedRowIds}
            onRowClick={allowRowClick ? onRowClick : undefined}
            getRowId={getRowIdentifier}
            hover={hover}
            striped={striped}
          />
        </MuiTable>
      </TableContainer>

      {pagination && (
        <TablePagination
          component="div"
          count={pagination.totalRows}
          page={pagination.page}
          rowsPerPage={pagination.rowsPerPage}
          onPageChange={pagination.onPageChange}
          onRowsPerPageChange={pagination.onRowsPerPageChange}
          rowsPerPageOptions={pagination.rowsPerPageOptions || [5, 10, 25, 50]}
        />
      )}
    </>
  );
};
